import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Paper,
  Chip,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as CopyIcon,
  ContentCopy as ContentCopyIcon,
  MoreVert as MoreVertIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import promptService, { Prompt, PromptSection } from '../services/promptService';
import { useAuth } from '../context/AuthContext';
import { tenantService, Tenant } from '../services/tenantService';
import { formatDate } from '../utils/dateUtils';

const PromptManagementPage: React.FC = () => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState<number | ''>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [formData, setFormData] = useState<{
    name: string;
    general: string;
    sections: PromptSection[];
  }>({
    name: '',
    general: '',
    sections: [],
  });

  const { user: currentUser } = useAuth();

  useEffect(() => {
    fetchPrompts();
    if (currentUser?.role === 'admin') {
      fetchTenants();
    }
  }, [currentUser]);

  const fetchTenants = async () => {
    try {
      const data = await tenantService.getTenants();
      setTenants(data);
    } catch (err: any) {
      console.error('Failed to load tenants:', err);
    }
  };

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      setError(null);
      // Pass undefined for is_active to get all prompts (active and inactive)
      const data = await promptService.getPrompts(undefined, undefined);
      setPrompts(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, prompt: Prompt) => {
    setAnchorEl(event.currentTarget);
    setSelectedPrompt(prompt);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    if (!selectedPrompt) return;
    setFormData({
      name: selectedPrompt.name,
      general: selectedPrompt.general || '',
      sections: selectedPrompt.sections || [],
    });
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleOpenCopyDialog = () => {
    if (!selectedPrompt) return;
    setFormData({
      name: selectedPrompt.name,
      general: selectedPrompt.general || '',
      sections: selectedPrompt.sections || [],
    });
    setSelectedTenantId(currentUser?.tenant_id || '');
    setCopyDialogOpen(true);
    handleMenuClose();
  };

  const handleCopyToTenant = async () => {
    if (!selectedPrompt || !currentUser?.tenant_id) {
      setError('You must belong to a tenant to copy prompts');
      return;
    }

    try {
      await promptService.copyToTenant(selectedPrompt.id, currentUser.tenant_id);
      setError(null);
      fetchPrompts();
      alert('Prompt copied successfully!');
      handleMenuClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to copy prompt');
    }
  };

  const handleSaveCopy = async () => {
    if (!selectedPrompt || !selectedTenantId) {
      setError('Please select a tenant');
      return;
    }

    try {
      await promptService.createPrompt({
        tenant_id: selectedTenantId as number,
        prompt_type: selectedPrompt.prompt_type,
        name: formData.name,
        general: formData.general,
        sections: formData.sections,
        is_global: false,
        is_active: true,
      });
      setCopyDialogOpen(false);
      setSelectedPrompt(null);
      setSelectedTenantId('');
      fetchPrompts();
      setError(null);
      alert('Tenant-specific prompt created successfully!');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create prompt');
    }
  };

  const handleSave = async () => {
    if (!selectedPrompt) return;

    try {
      await promptService.updatePrompt(selectedPrompt.id, {
        name: formData.name,
        general: formData.general,
        sections: formData.sections,
      });
      setEditDialogOpen(false);
      setSelectedPrompt(null);
      fetchPrompts();
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save prompt');
    }
  };

  const handleDelete = async () => {
    if (!selectedPrompt || !window.confirm(`Are you sure you want to delete "${selectedPrompt.name}"?`)) {
      return;
    }

    try {
      await promptService.deletePrompt(selectedPrompt.id);
      fetchPrompts();
      setError(null);
      handleMenuClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete prompt');
    }
  };

  const handleAddSection = () => {
    setFormData({
      ...formData,
      sections: [...formData.sections, { title: '', prompt: '' }],
    });
  };

  const handleRemoveSection = (index: number) => {
    const newSections = formData.sections.filter((_, i) => i !== index);
    setFormData({ ...formData, sections: newSections });
  };

  const handleSectionChange = (index: number, field: 'title' | 'prompt', value: string) => {
    const newSections = [...formData.sections];
    newSections[index][field] = value;
    setFormData({ ...formData, sections: newSections });
  };

  const canEdit = (prompt: Prompt) => {
    if (prompt.is_global) {
      return currentUser?.role === 'admin';
    }
    return true; // Can edit tenant-specific prompts
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Prompt Management</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Prompt Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Sections</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {prompts.map((prompt) => (
              <TableRow key={prompt.id}>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight="medium">
                      {prompt.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={prompt.prompt_type} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  {prompt.is_global ? (
                    <Chip label="Global" color="primary" size="small" />
                  ) : (
                    <Chip label={prompt.tenant_name || `Tenant ${prompt.tenant_id}`} color="secondary" size="small" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>{prompt.sections?.length || 0}</TableCell>
                <TableCell>
                  <Chip
                    label={prompt.is_active ? 'Active' : 'Inactive'}
                    color={prompt.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatDate(prompt.created_at)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, prompt)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedPrompt && canEdit(selectedPrompt) && (
          <MenuItem onClick={handleEdit}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
        )}
        {selectedPrompt?.is_global && currentUser?.role === 'admin' && (
          <MenuItem onClick={handleOpenCopyDialog}>
            <ContentCopyIcon sx={{ mr: 1 }} fontSize="small" />
            Create Tenant Copy
          </MenuItem>
        )}
        {selectedPrompt?.is_global && currentUser?.tenant_id && (
          <MenuItem onClick={handleCopyToTenant}>
            <CopyIcon sx={{ mr: 1 }} fontSize="small" />
            Quick Copy to My Tenant
          </MenuItem>
        )}
        {selectedPrompt && canEdit(selectedPrompt) && (
          <MenuItem onClick={handleDelete}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" color="error" />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Prompt</DialogTitle>
        <DialogContent>
          {selectedPrompt && !selectedPrompt.is_global && selectedPrompt.tenant_name && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Tenant: {selectedPrompt.tenant_name}
            </Alert>
          )}
          {selectedPrompt && selectedPrompt.is_global && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Scope: Global
            </Alert>
          )}
          <TextField
            fullWidth
            label="Prompt Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
          />

          <TextField
            fullWidth
            label="General Prompt"
            value={formData.general}
            onChange={(e) => setFormData({ ...formData, general: e.target.value })}
            margin="normal"
            multiline
            rows={6}
            placeholder="Enter the general prompt text..."
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Sections</Typography>
            <Button startIcon={<AddIcon />} onClick={handleAddSection} size="small">
              Add Section
            </Button>
          </Box>

          {formData.sections.map((section, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="subtitle2">Section {index + 1}</Typography>
                <IconButton size="small" onClick={() => handleRemoveSection(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                fullWidth
                label="Section Title"
                value={section.title}
                onChange={(e) => handleSectionChange(index, 'title', e.target.value)}
                margin="dense"
                size="small"
              />
              <TextField
                fullWidth
                label="Section Prompt"
                value={section.prompt}
                onChange={(e) => handleSectionChange(index, 'prompt', e.target.value)}
                margin="dense"
                size="small"
                multiline
                rows={3}
              />
            </Paper>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy to Tenant Dialog */}
      <Dialog open={copyDialogOpen} onClose={() => setCopyDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create Tenant-Specific Copy</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Select Tenant</InputLabel>
            <Select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value as number)}
              label="Select Tenant"
            >
              <MenuItem value="">
                <em>Select a tenant</em>
              </MenuItem>
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Prompt Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
          />

          <TextField
            fullWidth
            label="General Prompt"
            value={formData.general}
            onChange={(e) => setFormData({ ...formData, general: e.target.value })}
            margin="normal"
            multiline
            rows={6}
            placeholder="Enter the general prompt text..."
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Sections</Typography>
            <Button startIcon={<AddIcon />} onClick={handleAddSection} size="small">
              Add Section
            </Button>
          </Box>

          {formData.sections.map((section, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="subtitle2">Section {index + 1}</Typography>
                <IconButton size="small" onClick={() => handleRemoveSection(index)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
              <TextField
                fullWidth
                label="Section Title"
                value={section.title}
                onChange={(e) => handleSectionChange(index, 'title', e.target.value)}
                margin="dense"
                size="small"
              />
              <TextField
                fullWidth
                label="Section Prompt"
                value={section.prompt}
                onChange={(e) => handleSectionChange(index, 'prompt', e.target.value)}
                margin="dense"
                size="small"
                multiline
                rows={3}
              />
            </Paper>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveCopy} variant="contained" color="primary" disabled={!selectedTenantId}>
            Create Copy
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptManagementPage;
